/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiLink,
  EuiPageHeader,
  EuiSkeletonText,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import Breadcrumbs from '@modules/cui/Breadcrumbs/Breadcrumbs'
import { useGetProjectQuery } from '@modules/project-user-lib/hooks/get'
import { projectNetworkSecurityCrumbs } from '@modules/project-user-pages/breadcrumbs'
import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import { useListServerlessTrafficFiltersQuery } from '@modules/network-security-lib/hooks/serverless'
import TrafficFilterAssociationList from '@modules/network-security-components/TrafficFilterAssociationList'
import type { NormalizedTrafficFilterInfo, RulesetType } from '@modules/network-security-lib/types'
import TrafficFilterAssociationForm from '@modules/network-security-components/TrafficFilterAssociationForm'
import { networkSecurityUrl } from '@modules/network-security-pages/urls'
import ApplyButton from '@modules/network-security-components/TrafficFilterAssociationList/ApplyButton'

import emptyStateIllustration from './emptyStateIllustration.svg'

type Props = {
  projectType: ProjectType
}

const UserProjectNetworkSecurityPage: React.FunctionComponent<Props> = ({ projectType }) => {
  const { projectId } = useParams() as { projectId: ProjectID }
  const navigate = useNavigate()

  const { data: project, error: projectError } = useGetProjectQuery(projectType, projectId)

  const { data: rulesets, isLoading: isLoadingRulesets } = useListServerlessTrafficFiltersQuery()

  const [applyingRulesetType, setApplyingRulesetType] = useState<RulesetType | null>(null)

  const items: NormalizedTrafficFilterInfo[] = useMemo(() => {
    if (isLoadingRulesets) {
      return []
    }

    return (project?.traffic_filters || [])
      .map(({ id }) => rulesets?.items.find((ruleset) => ruleset.id === id))
      .filter((ruleset) => ruleset !== undefined)
      .map((ruleset) => ({
        type: 'serverless' as const,
        ruleset,
        associations: [{ id: projectId }],
      }))
  }, [isLoadingRulesets, project])

  if (projectError) {
    return <ProjectApiErrorCallout error={projectError} />
  }

  if (!project) {
    return <EuiSkeletonText />
  }

  const flyout = applyingRulesetType && (
    <TrafficFilterAssociationForm
      onClose={() => {
        setApplyingRulesetType(null)
      }}
      rulesetType={applyingRulesetType}
      project={project}
    />
  )

  if (!project.traffic_filters?.length) {
    return (
      <div>
        {renderEmptyState()}
        {flyout}
      </div>
    )
  }

  const pageTitle = (
    <FormattedMessage id='projectNetworkSecurity.title' defaultMessage='Network security' />
  )

  const intro = (
    <FormattedMessage
      id='projectNetworkSecurity.intro'
      defaultMessage='Specify the traffic allowed to connect to the project. Traffic matching any filter is allowed, all other traffic is denied. Traffic filters can be created in your account.'
    />
  )

  return (
    <div>
      <Breadcrumbs
        breadcrumbs={projectNetworkSecurityCrumbs({
          projectType,
          projectId,
          projectName: project.name,
        })}
      />

      <EuiPageHeader pageTitle={pageTitle} description={intro} bottomBorder={true} />

      <EuiSpacer />

      <TrafficFilterAssociationList
        items={items}
        isLoading={isLoadingRulesets}
        showSearch={false}
        onClickApply={setApplyingRulesetType}
        project={project}
      />

      {flyout}
    </div>
  )

  function renderEmptyState() {
    return (
      <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
        <EuiSpacer size='xxl' />
        <EuiFlexGroup gutterSize='xl' alignItems='flexStart'>
          <EuiFlexItem grow={3}>
            <EuiTitle size='l'>
              <h1>
                <FormattedMessage
                  id='projectNetworkSecurity.title'
                  defaultMessage='Network security'
                />
              </h1>
            </EuiTitle>

            <EuiSpacer size='m' />

            <EuiText>
              <FormattedMessage
                id='projectNetworkSecurity.intro'
                defaultMessage="<p>Protect your Elastic Cloud resources by applying IP filter or private connection policies. Traffic matching any applied policy is allowed, and all other traffic is denied.</p><p>New policies can be created in your organization's <link>Network security settings</link>.</p>"
                values={{
                  p: (content) => <p>{content}</p>,
                  link: (content) => (
                    <EuiLink
                      href={networkSecurityUrl()}
                      onClick={(e) => {
                        e.preventDefault()
                        navigate(networkSecurityUrl())
                      }}
                    >
                      {content}
                    </EuiLink>
                  ),
                }}
              />
            </EuiText>

            <ApplyButton
              onClick={setApplyingRulesetType}
              fill={true}
              disablePrivateConnection={Boolean(project) || undefined}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={2}>
            <img src={emptyStateIllustration} alt='Illustration of empty box' />
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    )
  }
}

export default UserProjectNetworkSecurityPage
